import React, { useState, useEffect } from 'react';
import { FaVolumeUp, FaVolumeMute } from 'react-icons/fa';
import { QuizQuestion, AnswerFeedback } from './types';
import { getCompletionPercentage } from './utils/helpers';
import SpotlightAnimation from './SpotlightAnimation';
import KBCUserProfile from './KBCUserProfile';

interface QuestionCardProps {
  question: QuizQuestion;
  questionNumber: number;
  totalQuestions: number;
  onAnswer: (optionId: string) => void;
  onNextQuestion: () => void;
  onQuitClick: () => void;
  feedback: AnswerFeedback;
  correctOption?: string;
  disabled?: boolean;
  audioEnabled: boolean;
  onToggleAudio: () => void;
}

const QuestionCard: React.FC<QuestionCardProps> = ({
  question,
  questionNumber,
  totalQuestions,
  onAnswer,
  onNextQuestion,
  onQuitClick,
  feedback,
  correctOption,
  disabled = false,
  audioEnabled,
  onToggleAudio,
}) => {
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [isLocked, setIsLocked] = useState(false);
  const [showCard, setShowCard] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(60);
  const lockTimeoutRef = React.useRef<number | null>(null);
  const progress = getCompletionPercentage(questionNumber, totalQuestions);

  // Reset selected option and lock state when question changes
  useEffect(() => {
    setSelectedOption(null);
    setIsLocked(false);
    setTimeRemaining(60); // Reset timer to 60 seconds
    // Clear any pending lock timeout from previous question
    if (lockTimeoutRef.current) {
      window.clearTimeout(lockTimeoutRef.current);
      lockTimeoutRef.current = null;
    }
  }, [question.id]);

  // Cleanup on unmount: clear any pending lock timeout
  useEffect(() => {
    return () => {
      if (lockTimeoutRef.current) {
        window.clearTimeout(lockTimeoutRef.current);
        lockTimeoutRef.current = null;
      }
    };
  }, []);

  // Question reveal sequence: hide card -> show spotlight (5s) -> reveal card
  useEffect(() => {
    // Step 1: Hide card when new question loads
    setShowCard(false);
    
    // Step 2: Wait for spotlight animation + audio to complete (5000ms)
    const revealTimer = setTimeout(() => {
      setShowCard(true);
    }, 5000);

    return () => clearTimeout(revealTimer);
  }, [question.id]);

  // Timer countdown - only when card is shown and no feedback yet
  useEffect(() => {
    if (!showCard || feedback !== 'none' || isLocked) return;

    let tickAudio: HTMLAudioElement | null = null;

    const timerInterval = setInterval(() => {
      setTimeRemaining((prev) => {
        // Play tick sound for last 5 seconds (only if audio enabled)
        if (prev <= 5 && prev > 0 && audioEnabled) {
          try {
            // Play a tick/beep sound (placeholder - add your own audio file)
            tickAudio = new Audio('/audio/tick.wav');
            // FAILSAFE: Set volume based on audio state
            tickAudio.volume = audioEnabled ? 0.5 : 0;
            tickAudio.play().catch(() => {
              console.debug('Tick audio not available');
            });
          } catch (error) {
            console.debug('Tick audio error');
          }
        }

        if (prev <= 1) {
          // Time's up! Auto-submit wrong answer
          clearInterval(timerInterval);
          setIsLocked(true);
          // Submit empty/wrong answer after short delay
          setTimeout(() => {
            onAnswer(''); // Empty string = timeout
          }, 300);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearInterval(timerInterval);
      if (tickAudio) {
        tickAudio.pause();
      }
    };
  }, [showCard, feedback, isLocked, onAnswer, audioEnabled]);

  const handleOptionClick = (optionId: string) => {
    if (disabled || feedback !== 'none' || isLocked) return;
    
    // Allow changing selection before locking
    setSelectedOption(optionId);
  };

  const handleLockAnswer = () => {
    if (!selectedOption || isLocked) return;
    
    setIsLocked(true);
    // Wait 3 seconds before revealing answer to build suspense
    if (lockTimeoutRef.current) {
      window.clearTimeout(lockTimeoutRef.current);
    }
    lockTimeoutRef.current = window.setTimeout(() => {
      onAnswer(selectedOption);
      lockTimeoutRef.current = null;
    }, 3000);
  };

  const getOptionClassName = (optionId: string): string => {
    const baseClasses = 'option-hexagon w-full font-medium transition-all duration-300 transform relative border rounded-lg';
    
    // If no answer locked yet
    if (feedback === 'none' && !isLocked) {
      // Selected but not locked - AMBER BORDER
      if (selectedOption === optionId) {
        return `${baseClasses} bg-gradient-to-r from-blue-900 to-blue-800 text-kbc-amber scale-105 cursor-pointer shadow-[0_0_20px_rgba(255,165,0,0.5)]`;
      }
      // Not selected - default state with blue border, hover to amber
      return `${baseClasses} bg-gradient-to-r from-blue-950 to-blue-900 hover:from-blue-900 hover:to-blue-800 text-blue-400/70 hover:text-kbc-amber hover:scale-105 cursor-pointer active:scale-95 hover:shadow-[0_0_15px_rgba(255,165,0,0.3)]`;
    }

    // If this option is selected and locked/answered
    if (selectedOption === optionId) {
      if (feedback === 'correct') {
        // Correct answer - GREEN FILL with GOLD BORDER
        return `${baseClasses} bg-gradient-to-r from-green-700 to-green-600 text-kbc-gold scale-105 shadow-[0_0_25px_rgba(255,215,0,0.7)]`;
      }
      if (feedback === 'wrong') {
        // Wrong answer - RED FILL with RED BORDER
        return `${baseClasses} bg-gradient-to-r from-red-700 to-red-600 text-kbc-error scale-105 shadow-[0_0_25px_rgba(220,20,60,0.7)]`;
      }
      // Locked but not yet answered (timeout interval) - AMBER FILL with GOLD BORDER
      return `${baseClasses} bg-gradient-to-r from-amber-600 to-amber-500 text-kbc-gold scale-105 shadow-[0_0_25px_rgba(255,215,0,0.6)]`;
    }

    // Show correct answer after wrong selection - GREEN FILL with GOLD BORDER
    if (feedback === 'wrong' && correctOption === optionId) {
      return `${baseClasses} bg-gradient-to-r from-green-700 to-green-600 text-kbc-gold shadow-[0_0_25px_rgba(255,215,0,0.7)]`;
    }

    // Other options (disabled state)
    return `${baseClasses} bg-gradient-to-r from-blue-950/40 to-blue-900/40 text-blue-400/20 cursor-not-allowed`;
  };

  // Dynamic font size based on text length - button size NEVER changes, only font
  // More aggressive sizing to ensure ALL text is always visible
  const getOptionTextSize = (text: string): string => {
    if (text.length > 80) return 'text-[0.65rem]'; // Extra long - 10.4px
    if (text.length > 65) return 'text-[0.7rem]'; // Very long - 11.2px
    if (text.length > 50) return 'text-xs'; // Long - 12px
    if (text.length > 35) return 'text-sm'; // Medium - 14px
    if (text.length > 20) return 'text-base'; // Short-medium - 16px
    return 'text-base md:text-lg'; // Short - 16px/18px
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-950 via-purple-950 to-blue-900 p-4 flex items-center justify-center relative overflow-hidden">
      {/* Background ambient glow */}
      <div className="absolute inset-0 bg-gradient-radial from-amber-500/10 via-transparent to-transparent pointer-events-none" 
           style={{ 
             background: 'radial-gradient(ellipse 70% 50% at center 40%, rgba(255, 191, 0, 0.08) 0%, rgba(139, 69, 255, 0.05) 40%, transparent 70%)'
           }} 
      />
      
      {/* User Profile - Top Left (Small) */}
      <KBCUserProfile size="small" position="top-left" />
      
      {/* KBC Theatrical Spotlight Effect */}
      <SpotlightAnimation questionId={question.id} feedback={feedback} />
      
      <div className={`max-w-5xl w-full relative z-10 transition-all duration-700 pt-16 md:pt-0 ${
        showCard ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
      }`}>
        {/* Progress Bar */}
        <div className="mb-6">
          <div className="flex justify-between items-center text-white text-sm mb-2">
            <span>Question {questionNumber} of {totalQuestions}</span>
            <div className="flex items-center gap-4">
              <span>{progress}%</span>
              {feedback === 'none' && (
                <button
                  onClick={onQuitClick}
                  className="text-kbc-error hover:text-red-400 font-medium transition flex items-center gap-1 text-xs"
                  title="Walk out of quiz"
                >
                  <span>🚪</span>
                  <span>Quit</span>
                </button>
              )}
            </div>
          </div>
          <div className="w-full bg-white/20 rounded-full h-2 overflow-hidden">
            <div
              className="bg-kbc-gold h-full rounded-full transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Question Card */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 md:p-14 border border-white/20 shadow-2xl mb-6">
          {/* Prize Level Badge */}
          <div className="flex justify-between items-center mb-4">
            <span className="bg-kbc-gold text-maersk-navy px-4 py-2 rounded-full font-bold text-sm">
              Level {question.level}
            </span>
            {question.imageUrl && (
              <span className="text-2xl">🖼️</span>
            )}
          </div>

          {/* Question Text */}
          <h2 className="text-xl md:text-2xl text-white font-semibold mb-4 leading-relaxed">
            {question.text}
          </h2>

          {/* Timer Circle */}
          <div className="flex justify-center mb-6">
            <div className={`relative w-20 h-20 rounded-full flex items-center justify-center ${
              timeRemaining <= 5 ? 'animate-pulse' : ''
            }`}>
              {/* Timer circle background */}
              <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 100 100">
                {/* Background circle */}
                <circle
                  cx="50"
                  cy="50"
                  r="45"
                  fill="rgba(0, 44, 117, 0.5)"
                  stroke="rgba(255, 215, 0, 0.3)"
                  strokeWidth="3"
                />
                {/* Progress circle */}
                <circle
                  cx="50"
                  cy="50"
                  r="45"
                  fill="none"
                  stroke={timeRemaining <= 5 ? '#DC143C' : '#FFD700'}
                  strokeWidth="3"
                  strokeDasharray={`${(timeRemaining / 60) * 283} 283`}
                  strokeLinecap="round"
                  className="transition-all duration-1000 ease-linear"
                />
              </svg>
              {/* Timer number */}
              <div className={`text-2xl font-bold z-10 ${
                timeRemaining <= 5 ? 'text-kbc-error' : 'text-white'
              }`}>
                {timeRemaining}
              </div>
            </div>
          </div>

          {/* Optional Image */}
          {question.imageUrl && (
            <div className="mb-6 rounded-lg overflow-hidden">
              <img
                src={question.imageUrl}
                alt="Question illustration"
                className="w-full h-48 object-cover"
              />
            </div>
          )}

          {/* Options - KBC Style 2x2 Grid with FIXED button dimensions */}
          <div className="grid grid-cols-1 portrait:md:grid-cols-1 landscape:grid-cols-2 md:grid-cols-2 gap-y-6 gap-x-6 md:gap-y-7 md:gap-x-10 mb-6">
            {question.options.map((option) => (
              <button
                key={option.id}
                onClick={() => handleOptionClick(option.id)}
                disabled={disabled || feedback !== 'none' || isLocked}
                className={getOptionClassName(option.id)}
                style={{ 
                  width: '100%',
                  height: '68px', // FIXED HEIGHT
                  minHeight: '68px',
                  maxHeight: '68px'
                }}
              >
                <div className="option-content">
                  <span className={`font-bold mr-2 uppercase bg-white/20 px-2.5 py-1 rounded flex-shrink-0 ${getOptionTextSize(option.text)}`}>
                    {option.id}
                  </span>
                  <span className={`flex-1 text-left text-white ${getOptionTextSize(option.text)}`}
                        style={{ 
                          lineHeight: '1.2',
                          wordWrap: 'break-word',
                          overflowWrap: 'break-word',
                          display: 'block'
                        }}>
                    {option.text}
                  </span>
                  {selectedOption === option.id && feedback === 'correct' && (
                    <span className="ml-2 text-lg text-white flex-shrink-0">✓</span>
                  )}
                  {selectedOption === option.id && feedback === 'wrong' && (
                    <span className="ml-2 text-lg text-white flex-shrink-0">✗</span>
                  )}
                  {feedback === 'wrong' && correctOption === option.id && (
                    <span className="ml-2 text-lg text-white flex-shrink-0">✓</span>
                  )}
                </div>
              </button>
            ))}
          </div>

          {/* Lock Button */}
          {selectedOption && !isLocked && feedback === 'none' && (
            <div className="text-center animate-scale-in">
              <button
                onClick={handleLockAnswer}
                className="bg-gradient-to-r from-green-700 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold text-lg px-8 py-4 rounded-lg shadow-xl transform transition hover:scale-110 active:scale-95 border-2 border-white/30"
              >
                🔒 Lock kiya jaaye?
              </button>
              <p className="text-white/60 text-sm mt-2">
                Click to confirm your answer
              </p>
            </div>
          )}

          {/* Explanation (shown after answer) */}
          {feedback !== 'none' && question.explanation && (
            <div className="mt-6 p-4 bg-white/10 rounded-lg border border-white/20 animate-fade-in">
              <p className="text-white/90">
                <span className="font-bold text-kbc-gold">💡 Explanation: </span>
                {question.explanation}
              </p>
            </div>
          )}
        </div>

        {/* Feedback Message */}
        {feedback === 'correct' && (
          <div className="text-center space-y-4 animate-scale-in">
            <div className="text-kbc-success text-xl font-bold">
              🎉 Correct! Well done!
            </div>
            <button
              onClick={onNextQuestion}
              className="bg-maersk-blue hover:bg-opacity-80 text-maersk-navy font-bold text-lg px-8 py-4 rounded-lg shadow-xl transform transition hover:scale-110 active:scale-95 border-2 border-white/30"
            >
              Next Question →
            </button>
          </div>
        )}
        {feedback === 'wrong' && (
          <div className="text-center space-y-4 animate-scale-in">
            <div className="text-kbc-error text-xl font-bold">
              ❌ Wrong answer! Better luck next time!
            </div>
            <button
              onClick={onNextQuestion}
              className="bg-maersk-blue hover:bg-opacity-80 text-maersk-navy font-bold text-lg px-8 py-4 rounded-lg shadow-xl transform transition hover:scale-110 active:scale-95 border-2 border-white/30"
            >
              View Results →
            </button>
          </div>
        )}
      </div>

      {/* Audio Toggle Button */}
      <div className="absolute bottom-4 right-4 z-20">
        <button
          onClick={onToggleAudio}
          className="bg-white/10 hover:bg-white/20 text-white/80 hover:text-white font-medium py-2 px-4 rounded-lg border border-white/20 transition flex items-center justify-center gap-2 text-sm"
        >
          {!audioEnabled ? <FaVolumeMute className="text-lg" /> : <FaVolumeUp className="text-lg" />}
          <span>{!audioEnabled ? 'Sound Off' : 'Sound On'}</span>
        </button>
      </div>
    </div>
  );
};

export default QuestionCard;
